import { useCallback, useEffect, useState } from "react";
import CssBaseline from "@mui/material/CssBaseline";
import { useNavigate } from "react-router-dom";
import {
  deleteCategory,
  fetchCategoriesPaginated,
  getCategoriesError,
  getCategoriesStatus,
  getCategoriesSuccess,
  selectAllCategories,
  selectCategoriesPagination,
  selectCategoriesLastParams,
  selectCategoriesQuery,
  setCategoriesQuery,
  resetCategoriesQuery,
  updateStatus,
} from "../../../../features/categorySlice";
import { useDispatch, useSelector } from "react-redux";
import Loading from "../../../../components/loading";
import Error from "../../../../components/Error";
import AppBarSearch from "../../../../global/appBarSearch";
import { toast } from "react-toastify";
import { Box, Grid, TablePagination, Typography } from "@mui/material";
import NoData from "../../../../components/noData";
import { useRestaurant } from "../../../../hooks/RestaurantContext";
import ProductCard from "../../../../components/card";
import ListViewCategory from "./listViewCategory";
import {
  ResponsiveProvider,
  useIsCompactHeader,
} from "../../../../hooks/uiHook";
import AlertDialog from "../../../../components/alertDialog";
import { useTranslation } from "react-i18next";

const Category = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { restaurantId } = useRestaurant();
  const isCompactHeader = useIsCompactHeader();
  const [orderedCategories, setOrderedCategories] = useState([]);
  const query = useSelector(selectCategoriesQuery);
  const { search, limit } = query;
  const page = query.page - 1;
  const [open, setOpen] = useState(false);
  const [cardId, setCardId] = useState(null);
  const [viewMode, setViewMode] = useState("grid");
  const categories = useSelector(selectAllCategories);
  const pagination = useSelector(selectCategoriesPagination);
  const lastFetchedParams = useSelector(selectCategoriesLastParams);
  const error = useSelector(getCategoriesError);
  const success = useSelector(getCategoriesSuccess);
  const status = useSelector(getCategoriesStatus);
  const totalCount = pagination.totalRecords || 0;
  // Reordering (drag & drop) relies on the index of the visible rows
  // reflecting the true, global position of each category. That only holds
  // true when everything fits on a single page, so drag reorder is disabled
  // once the list spans more than one page.
  const canReorder = (pagination.totalPages || 1) <= 1;

  const handleClickOpen = (cardId) => {
    setOpen(true);
    setCardId(cardId);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    navigate("add");
  };

  const handleModify = (data) => {
    navigate("modify", { state: { category: data } });
  };

  const handleProducts = (id, name) => {
    navigate("productsByCategory", {
      state: { categoryId: id, categoryName: name },
    });
  };

  // Reset the search/pagination query on every fresh mount (not just on
  // restaurant switch, handled separately below) so navigating away (e.g. to
  // Add/Modify, which unmounts this whole screen) and back always starts
  // from a clean search — the visible input already resets on remount since
  // it's local state inside AppBarSearch, so leaving the Redux-backed query
  // untouched made the search silently keep filtering while looking cleared.
  // Placed above the fetch effect so it's registered (and runs) first.
  useEffect(() => {
    dispatch(resetCategoriesQuery());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let effectiveSearch = search;
    let effectiveLimit = limit;
    let effectivePage = page + 1;

    if (lastFetchedParams) {
      let previousRestaurantId;
      try {
        previousRestaurantId = JSON.parse(lastFetchedParams).restaurantId;
      } catch {
        previousRestaurantId = undefined;
      }
      if (previousRestaurantId !== restaurantId) {
        effectiveSearch = "";
        effectiveLimit = 12;
        effectivePage = 1;
        dispatch(resetCategoriesQuery());
      }
    }

    const params = {
      restaurantId,
      search: effectiveSearch,
      limit: effectiveLimit,
      page: effectivePage,
    };
    const serialized = JSON.stringify(params);
    if (lastFetchedParams === serialized && status === "fetchDataPaginated") {
      return;
    }
    dispatch(fetchCategoriesPaginated(params));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, restaurantId, search, limit, page]);

  useEffect(() => {
    if (status === "fetchDataPaginated") setOrderedCategories(categories);
  }, [categories, status]);

  useEffect(() => {
    if (status === "deleteSuccess") {
      toast.success(success);
      setOrderedCategories((prev) => prev.filter((item) => item._id !== cardId));
      dispatch(updateStatus());
    } else if (status === "deleteError") {
      toast.error(error);
    }
  }, [status, error, success, dispatch, cardId]);

  const handleChangePage = useCallback(
    (event, newPage) => {
      const maxPage = Math.max(0, Math.ceil(totalCount / limit) - 1);
      const clampedPage = Math.min(newPage, maxPage);
      dispatch(setCategoriesQuery({ page: clampedPage + 1 }));
    },
    [dispatch, limit, totalCount]
  );

  const handleChangeRowsPerPage = useCallback(
    (event) => {
      const newLimit = Number(event.target.value);
      dispatch(setCategoriesQuery({ limit: newLimit, page: 1 }));
    },
    [dispatch]
  );

  const effectiveViewMode = isCompactHeader ? "grid" : viewMode;
  const isLoading = status === "loadingFetchDataPaginated";
  const isError = status === "fetchErrorPaginated";

  return (
    <div className="main-application">
      <CssBaseline />
      <ResponsiveProvider maxWidth={900}>
        <AppBarSearch
          handleSubmit={handleSubmit}
          handleSearch={(e) => {
            dispatch(setCategoriesQuery({ search: e.target.value, page: 1 }));
          }}
          title={t("pages.myCategories")}
          buttonTitle={t("actions.addCategory")}
          viewMode={effectiveViewMode}
          onToggleViewMode={
            isCompactHeader
              ? undefined
              : () => setViewMode(viewMode === "list" ? "grid" : "list")
          }
        />
      </ResponsiveProvider>
      <main>
        <Box sx={{ my: 2 }}>
          {!canReorder && effectiveViewMode === "list" && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
              {t("common.reorderRequiresSinglePage")}
            </Typography>
          )}
          {effectiveViewMode === "list" ? (
            <ListViewCategory
              handleClickOpen={handleClickOpen}
              handleModify={handleModify}
              handleProducts={handleProducts}
              filteredCategories={orderedCategories}
              setFilteredCategories={setOrderedCategories}
              status={status}
              error={error}
              search={search}
              disableDrag={!canReorder}
              loadingStatus="loadingFetchDataPaginated"
              errorStatus="fetchErrorPaginated"
            />
          ) : (
            <Box sx={{ my: 2 }}>
              <Grid container spacing={2}>
                {isLoading ? (
                  <Loading size={9} />
                ) : isError ? (
                  <Error>{error}</Error>
                ) : (
                  <>
                    {orderedCategories && orderedCategories.length > 0 ? (
                      orderedCategories.map((item) => (
                        <ProductCard
                          key={item._id}
                          data={item}
                          handleModify={() => handleModify(item)}
                          handleClickOpen={() => handleClickOpen(item._id)}
                          handleProducts={() =>
                            handleProducts(item._id, item.name)
                          }
                          search={search}
                        />
                      ))
                    ) : (
                      <NoData />
                    )}
                  </>
                )}
              </Grid>
            </Box>
          )}
          <TablePagination
            rowsPerPageOptions={[12, 24, 48]}
            component="div"
            count={totalCount}
            rowsPerPage={limit}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
          <AlertDialog
            handleClose={() => setOpen(false)}
            open={open}
            message={"Voulez-vous supprimer cette catégorie ?"}
            cardId={cardId}
            deleteData={() =>
              dispatch(deleteCategory({ categoryId: cardId, restaurantId }))
            }
          />
        </Box>
      </main>
    </div>
  );
};

export default Category;
